// Gestão de conexões — Fase 4.
//
// Aqui se cria e se derruba o canal por onde a empresa fala com o cliente, e
// por isso a tela é de admin do Inbox. O papel vem de `inbox_members`, e a edge
// function confere de novo do lado de lá: esconder o botão não é autorização.
//
// O front LÊ da view `inbox_connections_public` e AGE pela edge function. A
// tabela `inbox_channel_connections` nunca é consultada direto — ela guarda
// `credentials_ref`, e o grant é por coluna.

import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, Instagram, Loader2, Lock, Mail, MessageCircle,
  Plus, Power, QrCode, RefreshCw, RotateCw, ShieldAlert, Radio,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useInboxMembro } from '../../../src/hooks/useInboxMembro';
import type { ConexaoPublica } from './tipos';
import { carregarConexoes, carregarOrgId } from './api';
import {
  criarConexaoWhatsApp, definirWebhook, desconectarConexao, reiniciarConexao,
} from './apiConexoes';
import { desdeQuando } from './formato';
import ModalPareamento from './componentes/ModalPareamento';

const BASE_PADRAO = 'https://evo.vigorenergy.com.br';

const ROTULO_STATUS: Record<string, { texto: string; classe: string }> = {
  active:       { texto: 'Conectado',   classe: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  qr_pending:   { texto: 'Aguardando pareamento', classe: 'bg-secondary/20 text-[#9A6A12] dark:text-secondary' },
  pending:      { texto: 'Não pareado', classe: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300' },
  disconnected: { texto: 'Desconectado', classe: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300' },
  error:        { texto: 'Com erro',    classe: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};

export default function ConfiguracoesView() {
  const { profile } = useAuth();
  const { ehMembro, ehAdmin, carregando: carregandoAcesso } = useInboxMembro(profile?.id);

  const [conexoes, setConexoes] = useState<ConexaoPublica[]>([]);

  // Esta seção é a do pareamento por QR: "Parear", "Reiniciar" e "Desconectar"
  // só existem onde há uma instância da Evolution do outro lado. Sem o filtro,
  // a caixa de e-mail caía aqui dentro e a tela pedia à Evolution uma instância
  // com o nome do endereço — daí o erro `The "contato@atende…" instance does
  // not exist` em 22/09/2026.
  //
  // O filtro é pela CAPABILITY, não por `channel_type`: é a regra do módulo
  // (seção 6 do PRD), e é o que mantém a tela correta quando entrar outro canal
  // que também pareia por QR.
  const conexoesQr = conexoes.filter((c) => c.capabilities?.requiresQrPairing === true);
  const conexoesEmail = conexoes.filter((c) => c.capabilities?.supportsSubject === true);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [pareando, setPareando] = useState<ConexaoPublica | null>(null);

  const [mostrarForm, setMostrarForm] = useState(false);
  const [nome, setNome] = useState('');
  const [instancia, setInstancia] = useState('');
  const [baseUrl, setBaseUrl] = useState(BASE_PADRAO);
  const [criando, setCriando] = useState(false);

  const recarregar = useCallback(async () => {
    if (!profile?.id) return;
    setCarregando(true);
    try {
      const org = await carregarOrgId(profile.id);
      setOrgId(org);
      if (org) setConexoes(await carregarConexoes(org));
    } catch (e: any) {
      setErro(e.message ?? 'Falha ao carregar as conexões.');
    } finally {
      setCarregando(false);
    }
  }, [profile?.id]);

  useEffect(() => { void recarregar(); }, [recarregar]);

  const agir = useCallback(async (id: string, fn: () => Promise<unknown>) => {
    setOcupado(id);
    setErro(null);
    try {
      await fn();
      await recarregar();
    } catch (e: any) {
      setErro(e.message ?? 'A ação falhou.');
    } finally {
      setOcupado(null);
    }
  }, [recarregar]);

  const criar = useCallback(async () => {
    setCriando(true);
    setErro(null);
    try {
      const { conexao } = await criarConexaoWhatsApp({
        display_name: nome.trim(),
        instancia: instancia.trim().toLowerCase(),
        base_url: baseUrl.trim(),
      });
      setMostrarForm(false);
      setNome('');
      setInstancia('');
      await recarregar();
      // Já abre o QR: criar sem parear não serve para nada.
      const novas = orgId ? await carregarConexoes(orgId) : [];
      const nova = novas.find((c) => c.id === conexao.id);
      if (nova) setPareando(nova);
    } catch (e: any) {
      setErro(e.message ?? 'Não consegui criar a conexão.');
    } finally {
      setCriando(false);
    }
  }, [nome, instancia, baseUrl, orgId, recarregar]);

  if (carregandoAcesso || (carregando && conexoes.length === 0 && !erro)) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 size={22} className="animate-spin text-gray-300 dark:text-slate-600" />
      </div>
    );
  }

  if (!ehMembro || !ehAdmin) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-6 py-20 text-center">
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-slate-700 dark:text-slate-500">
          <Lock size={24} />
        </div>
        <h1 className="text-base font-bold text-gray-800 dark:text-white">Área restrita</h1>
        <p className="mt-1.5 text-sm text-gray-500 dark:text-slate-400">
          Só um administrador do Inbox mexe nas conexões: é aqui que se cria e se derruba o canal por
          onde a empresa inteira fala com o cliente.
        </p>
        <Link to="/inbox" className="mt-4 text-sm font-semibold text-primary hover:underline">
          Voltar para a caixa de entrada
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 pb-10 md:px-6">
      <header className="mb-5">
        <Link
          to="/inbox"
          className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 transition-colors hover:text-primary dark:text-slate-400"
        >
          <ArrowLeft size={13} />
          Caixa de entrada
        </Link>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Conexões</h1>
        <p className="text-sm text-gray-500 dark:text-slate-400">
          Os canais por onde as mensagens entram e saem.
        </p>
      </header>

      {erro && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-red-50 px-3.5 py-2.5 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span className="flex-1">{erro}</span>
          <button onClick={() => setErro(null)} className="shrink-0 font-semibold hover:underline">fechar</button>
        </div>
      )}

      {/* ─── WhatsApp ──────────────────────────────────────────────────── */}
      <section className="mb-6">
        <div className="mb-2.5 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-slate-200">
            <MessageCircle size={15} className="text-primary" />
            WhatsApp
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { void recarregar(); }}
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:text-primary dark:border-slate-700 dark:text-slate-300"
            >
              <RefreshCw size={12} className={carregando ? 'animate-spin' : ''} />
              Atualizar
            </button>
            <button
              onClick={() => setMostrarForm((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary/90"
            >
              <Plus size={13} />
              Nova conexão
            </button>
          </div>
        </div>

        {mostrarForm && (
          <div className="mb-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-medium text-gray-600 dark:text-slate-300">
                Nome que aparece na tela
                <input
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Comercial Vigor"
                  className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-normal text-gray-800 focus:border-primary focus:bg-white focus:outline-none dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-100"
                />
              </label>
              <label className="text-xs font-medium text-gray-600 dark:text-slate-300">
                Identificador da instância
                <input
                  value={instancia}
                  onChange={(e) => setInstancia(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="vigor-comercial"
                  className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm font-normal text-gray-800 focus:border-primary focus:bg-white focus:outline-none dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-100"
                />
              </label>
              <label className="text-xs font-medium text-gray-600 dark:text-slate-300 sm:col-span-2">
                Endereço da Evolution
                <input
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs font-normal text-gray-800 focus:border-primary focus:bg-white focus:outline-none dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-100"
                />
              </label>
            </div>
            <p className="mt-2.5 text-[11px] text-gray-400 dark:text-slate-500">
              O identificador não muda depois de criado — é por ele que a Evolution guarda a sessão do
              número. A ingestão de mensagens nasce desligada.
            </p>
            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={() => setMostrarForm(false)}
                className="rounded-xl px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-slate-400"
              >
                Cancelar
              </button>
              <button
                onClick={() => { void criar(); }}
                disabled={criando || !nome.trim() || !instancia.trim()}
                className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary/90 disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-slate-700"
              >
                {criando && <Loader2 size={12} className="animate-spin" />}
                Criar e parear
              </button>
            </div>
          </div>
        )}

        {conexoesQr.length === 0 && !mostrarForm && (
          <div className="rounded-2xl border border-dashed border-gray-300 px-6 py-10 text-center dark:border-slate-700">
            <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Nenhum número conectado</p>
            <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">
              Crie a conexão e pareie o WhatsApp do número comercial para as mensagens começarem a chegar.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          {conexoesQr.map((c) => {
            const rotulo = ROTULO_STATUS[c.status] ?? { texto: c.status, classe: 'bg-gray-100 text-gray-600' };
            const ingestaoLigada = c.metadata?.webhook_ligado === true;
            const trabalhando = ocupado === c.id;

            return (
              <div
                key={c.id}
                className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-bold text-gray-900 dark:text-white">{c.display_name}</h3>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${rotulo.classe}`}>
                        {rotulo.texto}
                      </span>
                      {c.capabilities?.isUnofficial && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          <ShieldAlert size={10} />
                          não oficial
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 font-mono text-[11px] text-gray-500 dark:text-slate-400">
                      {c.external_id}
                    </p>
                    <p className="mt-1 flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-slate-400">
                      <Radio size={11} className={c.status === 'active' ? 'text-emerald-500' : 'text-gray-400'} />
                      último sinal {desdeQuando(c.last_seen_at)}
                    </p>
                    {c.last_error && (
                      <p className="mt-1.5 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] text-red-700 dark:bg-red-900/20 dark:text-red-300">
                        {c.last_error}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      onClick={() => setPareando(c)}
                      disabled={trabalhando}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-50 dark:border-slate-600 dark:text-slate-300"
                    >
                      <QrCode size={12} />
                      Parear
                    </button>
                    <button
                      onClick={() => agir(c.id, () => reiniciarConexao(c.id))}
                      disabled={trabalhando}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:text-primary disabled:opacity-50 dark:border-slate-600 dark:text-slate-300"
                    >
                      <RotateCw size={12} className={trabalhando ? 'animate-spin' : ''} />
                      Reiniciar
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Desconectar ${c.display_name}? A sessão do celular é encerrada e nenhuma mensagem entra ou sai até parear de novo.`)) {
                          void agir(c.id, () => desconectarConexao(c.id));
                        }
                      }}
                      disabled={trabalhando}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300"
                    >
                      <Power size={12} />
                      Desconectar
                    </button>
                  </div>
                </div>

                {/* Ingestão: o interruptor que faz mensagem real entrar no banco. */}
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-gray-50 px-3 py-2.5 dark:bg-slate-900/50">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-700 dark:text-slate-200">
                      Ingestão de mensagens {ingestaoLigada ? 'ligada' : 'desligada'}
                    </p>
                    <p className="text-[11px] text-gray-500 dark:text-slate-400">
                      {ingestaoLigada
                        ? 'Tudo que chegar neste número é gravado no banco da Vigor.'
                        : 'O número pareia e responde, mas nada é gravado. Ligue só com o chip definitivo.'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      const msg = ingestaoLigada
                        ? 'Desligar a ingestão? As mensagens continuam chegando no celular, mas param de entrar no sistema.'
                        : 'Ligar a ingestão? A partir de agora TODA conversa deste número passa a ser gravada no banco da Vigor — inclusive as de quem não sabe disso. Só faça isso com o chip comercial.';
                      if (confirm(msg)) void agir(c.id, () => definirWebhook(c.id, !ingestaoLigada));
                    }}
                    disabled={trabalhando}
                    className={`shrink-0 rounded-xl px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
                      ingestaoLigada
                        ? 'border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600 dark:border-slate-600 dark:text-slate-300'
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                  >
                    {ingestaoLigada ? 'Desligar' : 'Ligar ingestão'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ─── Canais das próximas fases ─────────────────────────────────── */}
      <section>
        <h2 className="mb-2.5 text-sm font-bold text-gray-800 dark:text-slate-200">Outros canais</h2>
        <div className="grid gap-2.5 sm:grid-cols-2">
          {conexoesEmail.length === 0 ? (
            <div className="flex items-start gap-3 rounded-2xl border border-dashed border-gray-300 p-4 dark:border-slate-700">
              <Mail size={16} className="mt-0.5 shrink-0 text-gray-400" />
              <div>
                <p className="text-sm font-semibold text-gray-600 dark:text-slate-300">E-mail</p>
                <p className="text-[11px] text-gray-400 dark:text-slate-500">
                  Fase 7. Único canal com assunto, cópia e corpo formatado.
                </p>
              </div>
            </div>
          ) : (
            conexoesEmail.map((c) => (
              // A caixa de e-mail não pareia nem desconecta: ela só recebe. Por
              // isso aqui não há botão — o que importa é o endereço que o
              // cliente vê e o endereço por onde a mensagem entra, que são
              // diferentes de propósito (o segundo é o subdomínio de
              // recebimento, para o MX não tocar no domínio da empresa).
              <div
                key={c.id}
                className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900"
              >
                <Mail size={16} className="mt-0.5 shrink-0 text-gray-400" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-800 dark:text-slate-200">
                    {c.display_name}
                  </p>
                  <p className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-slate-400">
                    Recebe em <span className="font-medium">{c.external_id}</span>
                  </p>
                  {typeof c.metadata?.endereco_publico === 'string' && (
                    <p className="truncate text-[11px] text-gray-400 dark:text-slate-500">
                      O cliente responde para {c.metadata.endereco_publico as string}
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
          <div className="flex items-start gap-3 rounded-2xl border border-dashed border-gray-300 p-4 dark:border-slate-700">
            <Instagram size={16} className="mt-0.5 shrink-0 text-gray-400" />
            <div>
              <p className="text-sm font-semibold text-gray-600 dark:text-slate-300">Instagram e WhatsApp oficial</p>
              <p className="text-[11px] text-gray-400 dark:text-slate-500">
                Fases 12 e 13. Dependem da liberação da Meta.
              </p>
            </div>
          </div>
        </div>
      </section>

      {pareando && (
        <ModalPareamento
          conexao={pareando}
          onFechar={() => { setPareando(null); void recarregar(); }}
          onConectou={() => { void recarregar(); }}
        />
      )}
    </div>
  );
}
